import { useMemo, useState } from 'react';
import { ChevronDown, ChevronsUpDown, SearchX } from 'lucide-react';
import { IUserERC20Balance } from '@shared/types/frontend/assetPage';
import { networkNames } from '@shared/types/executor';
import { ACTIVE_NETWORKS, ChainId, NETWORK_LOGOS } from '@shared/utils/config/networks';
import Image from 'next/image';
import { CovalentBalanceItem } from '@shared/types/frontend';
import { TradeAssetsPageSchemaInputs } from '@shared/zod-schemas/frontend';
import { UseFormReturn } from 'react-hook-form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toSpecialFixed } from '@/lib/misc/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ImageWithBadge from '@/components/asset/sendFunds/ImageWithBadge';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import { Button } from '@/components/ui/button';
const PortfolioBalancesModalContent = ({
  assetData,
  form,
  handleToggleModal
}: {
  assetData: IUserERC20Balance;
  form: UseFormReturn<TradeAssetsPageSchemaInputs>;
  handleToggleModal: () => void;
}) => {
  const [network, setNetwork] = useState(ACTIVE_NETWORKS);
  const networks = [{
    networkName: 'All Networks',
    chainId: 'all-networks',
    networkLogo: '/static/genius.png'
  }, ...ACTIVE_NETWORKS.map(x => ({
    networkName: ChainId[x] || 'All Networks',
    chainId: x,
    networkLogo: NETWORK_LOGOS[x] || '/static/images/defaultnetwork.png'
  }))];
  const _handleOnSelectNetwork = (value: {
    networkName: string;
    chainId: number;
    networkLogo: any;
  } | {
    networkName: string;
    chainId: string;
    networkLogo: string;
  }) => {
    setNetwork(value.networkName === 'All Networks' ? ACTIVE_NETWORKS : [Number(value.chainId)]);
  };
  const onSelectToken = (_: CovalentBalanceItem) => {
    console.log('SelectFromToTrade:onSelectFromToken', {
      _
    });
    form.setValue('fromToken', _);
    if (form.watch('tokenAmount') !== '0' && form.watch('tokenAmount') !== '') {
      const _usdToToken = (parseFloat(form.watch('usdAmount')) / _.quote_rate).toFixed(3)?.toString();
      form.setValue('tokenAmount', _usdToToken);
    }
    handleToggleModal();
    return;
  };
  const [isOrderByHigherBalance, setIsOrderByHigherBalance] = useState(true);
  const _balancesBySelectedNetworks = useMemo(() => {
    if (!assetData) return [];
    return assetData.balances.filter(_token => network.some(_network => _token.network === _network && form.watch('fromToken')?.contract_address.toLowerCase() !== _token.contract_address.toLowerCase())).filter(_token => _token.usd_value && _token.usd_value > 0).sort((a, b) => {
      if (isOrderByHigherBalance) {
        return a.usd_value > b.usd_value ? -1 : 1;
      } else {
        return a.usd_value < b.usd_value ? -1 : 1;
      }
    });
  }, [assetData, network, isOrderByHigherBalance]);
  return <div className='grid gap-2' data-sentry-component="PortfolioBalancesModalContent" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
      <div className='flex justify-between px-2'>
        <p>Portfolio</p>
        {/* network filter */}
        <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
          <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
            <Button type='button' variant={network.length > 1 ? 'blue' : 'pink'} className={`flex gap-2 items-center rounded-full w-fit h-[26px] px-[7px] py-[3px]`} data-sentry-element="Button" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
              <Image src={network.length > 1 ? '/static/genius.png' : getNetworkLogo(network[0])} alt='selected-network-logo' width={100} height={100} className='w-[14px] h-[14px]' data-sentry-element="Image" data-sentry-source-file="PortfolioBalancesModalContent.tsx" />
              <p>
                {network.length > 1 ? 'All Networks' : networkNames[network[0]] === 'Binance Smart Chain' ? 'Binance' : networkNames[network[0]]}
              </p>
              <ChevronDown className='w-[12px] h-[12px]' data-sentry-element="ChevronDown" data-sentry-source-file="PortfolioBalancesModalContent.tsx" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='border-genius-blue bg-genius-indigo w-[154px] z-[3000]' data-sentry-element="DropdownMenuContent" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
            {networks.map(networkItem => <DropdownMenuItem key={networkItem.chainId} onSelect={() => _handleOnSelectNetwork(networkItem)}>
                <div className='flex gap-2 items-center'>
                  <Image src={networkItem.networkLogo} width={20} height={20} alt={`${networkItem.networkName}-logo`} className='h-[20px] w-[20px]' />
                  <span className='capitalize'>
                    {networkItem.networkName.replace('-mainnet', '')}
                  </span>
                </div>
              </DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* list header */}
      <div className='flex justify-between items-center bg-genius-blue p-2 relative'>
        <div className='w-[4000px] -translate-x-12 absolute bg-genius-blue h-14 z-10'></div>

        <p className='z-20'>Ticker</p>
        <Button type='button' variant='ghost' className='flex gap-2 items-center  z-20' onClick={() => setIsOrderByHigherBalance(!isOrderByHigherBalance)} data-sentry-element="Button" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
          <p>Balances</p>
          <ChevronsUpDown className='w-4 h-4' data-sentry-element="ChevronsUpDown" data-sentry-source-file="PortfolioBalancesModalContent.tsx" />
        </Button>
      </div>
      {/* balances list */}
      <ScrollArea className='h-[742px] pb-12' data-sentry-element="ScrollArea" data-sentry-source-file="PortfolioBalancesModalContent.tsx">
        <div className='grid gap-2'>
          {_balancesBySelectedNetworks.length > 0 ? <>
              {_balancesBySelectedNetworks.map((token, index) => <div key={`${index}-id`}>
                  {token.contract_name && <Button type='button' variant='ghost' key={`${token.contract_name}-id`} className='w-full flex justify-between items-center hover:bg-genius-blue py-8' onClick={() => onSelectToken(token)}>
                      <div className='flex items-center gap-4'>
                        <ImageWithBadge mainImageSrc={token.logo_url} badgeImageSrc={getNetworkLogo(token.network)} size={32} badgeSize={14} />
                        <div className='text-left'>
                          <p>{token.contract_display_name}</p>
                          <p className='opacity-50 text-xs'>{token.contract_ticker_symbol}</p>
                        </div>
                      </div>
                      <div className='text-right'>
                        <p>${toSpecialFixed(token.usd_value).value}</p>
                        <p className='opacity-50 text-xs'>
                          {toSpecialFixed(token.formatted_balance).value}
                          {token.contract_ticker_symbol}
                        </p>
                      </div>
                    </Button>}
                </div>)}
            </> : <div className='justify-items-center grid gap-4 mt-24'>
              <SearchX className='w-16 h-16 opacity-50' />
              <p className='opacity-50'>No balances</p>
            </div>}
        </div>
      </ScrollArea>
    </div>;
};
export default PortfolioBalancesModalContent;